// ADR 6.9.26p §1 — the window class is measured once per tab.
//
// `useWindowClass()` adds a `resize` listener and a piece of state per caller,
// so N callers are N listeners racing to agree. §4 needs the pane layout inside
// `PlanDetailPage`, which is not a child of `ShellBody` in the single-pane case,
// so a second call site already exists and a third will follow the first surface
// that wants one. The shell therefore calls the hook once and publishes it.
//
// ADR 7.10.26v §2/§4 — the provider now calls `useWindowReading()`, which wraps
// the same private measurement as `useWindowClass()`; the latter's `same()`
// semantics and immediate first re-read are untouched, so 5.9.26u's own tests
// stand. Two contexts come out of the one reading: the whole window's class,
// and the spread. `PageWindowClass` re-provides only the class, below the
// chrome, so every page composes for its own half with no edit of its own,
// while the spread stays one fact for the whole shell.

import { createContext, useContext, type ReactNode } from 'react'

import { useWindowReading, type WindowClass } from './useWindowClass'
import type { SpreadValue } from './spread/readSpread'

const WindowClassContext = createContext<WindowClass | null>(null)
const SpreadContext = createContext<SpreadValue | null>(null)

/** Calls `useWindowReading()` once and publishes both halves. The app's only caller. */
export function WindowClassProvider({ children }: { children: ReactNode }) {
  const { windowClass, spread } = useWindowReading()
  return (
    <SpreadContext.Provider value={spread}>
      <WindowClassContext.Provider value={windowClass}>{children}</WindowClassContext.Provider>
    </SpreadContext.Provider>
  )
}

/** ADR 7.10.26v §4 — one page's class for everything below it; the spread is not re-provided. */
export function PageWindowClass({ value, children }: { value: WindowClass; children: ReactNode }) {
  return <WindowClassContext.Provider value={value}>{children}</WindowClassContext.Provider>
}

/**
 * The published class — inside a page, that page's own.
 *
 * Throws outside the provider rather than falling back to a measurement of its
 * own: a surface that needs the class is inside the shell, and a silent second
 * reading is the exact divergence — one class laid out with, another reported —
 * that publishing exists to prevent. `renderWithSession` supplies the provider
 * so every suite gets the same guarantee the app has.
 */
export function useWindowClassValue(): WindowClass {
  const value = useContext(WindowClassContext)
  if (value === null) {
    throw new Error('useWindowClassValue() outside WindowClassProvider')
  }
  return value
}

/** ADR 7.10.26v §4 — the spread, readable anywhere in the shell. Throws outside the provider. */
export function useSpreadValue(): SpreadValue {
  const value = useContext(SpreadContext)
  if (value === null) {
    throw new Error('useSpreadValue() outside WindowClassProvider')
  }
  return value
}
